import React from 'react';
import { Box, CircularProgress, Fade } from '@mui/material';
import { SkeletonLoader } from './SkeletonLoader';
import { useHasChanged } from '@hooks/useHasChanged';

interface LoadingWrapperProps {
  loading: boolean;
  error?: boolean;
  errorComponent?: React.ReactNode;
  skeleton?: 'card' | 'list' | 'table' | 'form' | 'custom';
  skeletonCount?: number;
  customSkeleton?: React.ReactNode;
  fullHeight?: boolean;
  delay?: number;
  children: React.ReactNode;
}

export const LoadingWrapper: React.FC<LoadingWrapperProps> = ({
  loading,
  error = false,
  errorComponent,
  skeleton,
  skeletonCount = 1,
  customSkeleton,
  fullHeight = false,
  delay = 0,
  children,
}) => {
  const [delayElapsed, setDelayElapsed] = React.useState(false);
  if (useHasChanged(loading) && !loading) setDelayElapsed(false);
  const showLoading = delay === 0 || delayElapsed;

  React.useEffect(() => {
    if (loading && delay > 0) {
      const timer = setTimeout(() => setDelayElapsed(true), delay);
      return () => clearTimeout(timer);
    }
  }, [loading, delay]);

  if (error && errorComponent) {
    return <>{errorComponent}</>;
  }

  if (loading && showLoading) {
    if (skeleton) {
      return (
        <Fade in timeout={300}>
          <Box>
            {customSkeleton || (
              <SkeletonLoader variant={skeleton} count={skeletonCount} />
            )}
          </Box>
        </Fade>
      );
    }

    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: fullHeight ? '100vh' : '200px',
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      </Fade>
    );
  }

  return <Fade in timeout={300}>{children as React.ReactElement}</Fade>;
};
